export const SOCKET_SERVER_URL =
  process.env.REACT_APP_WS_URL ?? `http://${window.location.hostname}:8002`;
// Optional: Separate URL for receiving updates via API Gateway
export const UPDATE_SOCKET_URL = process.env.REACT_APP_WS_UPDATE_URL;
export const FRONT_OFFICE_LOCAL_URL =
  process.env.REACT_APP_FRONT_OFFICE_URI ?? `http://${window.location.hostname}:8666`;

function resolveBackofficeApiUrl(): string {
  if (process.env.REACT_APP_BACKOFFICE_API_URL) {
    return process.env.REACT_APP_BACKOFFICE_API_URL;
  }
  const domain = process.env.REACT_APP_DOMAIN;
  if (domain) {
    return `https://backoffice.${domain}/api`;
  }
  return `http://${window.location.hostname}:8665/api`;
}

export const BACKOFFICE_API_URL = resolveBackofficeApiUrl();

// Derive account URL: use env var if set, otherwise parse from hostname (production subdomain pattern)
function resolveAccountUrl(): string {
  if (process.env.REACT_APP_FRONT_OFFICE_URI) {
    return process.env.REACT_APP_FRONT_OFFICE_URI;
  }
  const segments = window.location.hostname.split('.');
  if (segments.length >= 3) {
    return `https://account.${segments[1]}.${segments[2]}`;
  }
  return `http://${window.location.hostname}:8666`;
}

export const ACCOUNT_URL = resolveAccountUrl();
export const cashierPath = 'hijack/cashier/deposit';
export const APP_DOMAIN = process.env.REACT_APP_DOMAIN;
const DOMAIN = (APP_DOMAIN || window.location.hostname)
  .replace(/^https?:\/\//, '')
  .replace(/\/+$/, '');
export const BACKOFFICE_API_BASE = `https://backoffice.${DOMAIN}/api`;
// The auth app always writes the web login handoff cookie as 'token-storage'
// (see apps/auth LoginApp.js / loginRedirectHelper.js). Some deployed game
// builds set REACT_APP_DESCOPE_COOKIE to Descope's native 'DS' name, which does
// NOT match the handoff cookie — so the game can't read the session that auth
// just wrote and bounces the player back to /loginapp. Normalize the legacy
// 'DS' value (and an unset value) to the name auth actually writes, so the
// handoff works regardless of the configured env. The native flow writes and
// reads under this same resolved name, so it stays internally consistent.
const HANDOFF_COOKIE_NAME = 'token-storage';
const configuredDescopeCookie = process.env.REACT_APP_DESCOPE_COOKIE;
export const DESCOPE_COOKIE =
  !configuredDescopeCookie || configuredDescopeCookie === 'DS'
    ? HANDOFF_COOKIE_NAME
    : configuredDescopeCookie;

// Engine REST base — REACT_APP_WS_URL normalized to http(s). Used for public
// engine endpoints like /public-config.
export const ENGINE_HTTP_URL = (SOCKET_SERVER_URL || `https://engine.${APP_DOMAIN}`)
  .replace(/^wss:\/\//, 'https://')
  .replace(/^ws:\/\//, 'http://');

// Referrals: the player-facing programme page and the API behind it.
//
// NOT derived from DOMAIN. In production the deploy deliberately sets
// REACT_APP_DOMAIN to hijack.poker, because the engine websocket and the auth
// app live there — but everything else, referrals included, is served from
// hijackpoker.com. Using DOMAIN here pointed the game at
// referrals.hijack.poker, a host the SPA is not deployed on, so the link 404d
// in production and nowhere else.
//
// REACT_APP_WEB_DOMAIN carries the unsplit web domain. The chain matches
// useSupportApi, which hit this same split and solved it the same way —
// including the literal production host at the end rather than
// window.location.hostname. That matters: the game is served from
// play.hijackpoker.com, so falling back to the hostname would produce
// referrals.play.hijackpoker.com, and falling back to APP_DOMAIN would
// reproduce the exact bug this fixes on any build made before the deploy
// started setting the variable.
const WEB_DOMAIN = (process.env.REACT_APP_WEB_DOMAIN || 'hijackpoker.com')
  .replace(/^https?:\/\//, '')
  .replace(/\/+$/, '');
export const REFERRALS_URL = `https://referrals.${WEB_DOMAIN}`;
export const REFERRALS_API_URL = `https://referrals-api.${WEB_DOMAIN}`;

// Serverless public-api / player-api bases. Derived from WEB_DOMAIN, the same
// fix as REFERRALS_URL/REFERRALS_API_URL and for the identical reason: both
// services deploy their custom domain from ssm:/{stage}/serverless/domain
// (public-api/player-api serverless.yml), which in production resolves to the
// `production` GitHub Environment's DOMAIN variable — hijackpoker.com, not the
// split hijack.poker used for the engine websocket/auth app. Built off DOMAIN
// (as these two originally were, the one pair REFERRALS_URL's own comment used
// to call out as NOT yet migrated), the frontend requested
// public-api.hijack.poker / player-api.hijack.poker — hosts neither service is
// deployed on — so every fetch through them (SNG payouts, the lobby listing)
// failed in production and nowhere else.
export const PUBLIC_API_URL = `https://public-api.${WEB_DOMAIN}`;

/**
 * player-api base. SET EXPLICITLY PER STAGE, not derived from another
 * feature's domain constant.
 *
 * It used to be `https://player-api.${DOMAIN}`, then was moved onto WEB_DOMAIN
 * by ce526e9d26 to fix SNG payouts in production. WEB_DOMAIN exists for the
 * referrals work (EN-10741) and is only written by deploy-web-app.yml, so that
 * move silently repointed every NON-PRODUCTION NATIVE build at production:
 * the three native workflows never set REACT_APP_WEB_DOMAIN, the fallback
 * below is the production host, and the fallback being plausible is exactly
 * what hid it. Measured consequence: in 26 hours on test2, player-api's
 * locationVerify was invoked 6 times, every one of them from web, and zero
 * times from either native client. The geofence had never run on native.
 *
 * Nothing about this URL should depend on a constant owned by a different
 * feature. A rename or a re-scope over there must not be able to move where
 * this app sends a player's location, its lobby request or its payout lookup.
 * REACT_APP_PLAYER_API_URL names this host directly, and every workflow that
 * builds this app sets it and FAILS THE BUILD if it is empty, so the
 * derivation below is reachable only from a local `npm start`.
 *
 * Kept as a fallback rather than removed, because the alternative for a
 * developer running locally is an empty origin and a fetch to a relative path,
 * which fails in a way that looks like anything but a missing env var.
 */
export const PLAYER_API_URL =
  (process.env.REACT_APP_PLAYER_API_URL || '').trim().replace(/\/+$/, '') ||
  `https://player-api.${WEB_DOMAIN}`;

// Global data CDN base (cdn.<domain>). Serves public, identical-for-every-
// viewer platform documents as CloudFront-cached S3 objects, one prefix per
// document type — see serverless-v2/services/global-data-cdn/serverless.yml.
// Shared across every document type, not activity-strip-specific — a future
// second document type reuses this SAME constant with its own path prefix,
// not a new one of its own. The activity-strip document (Tables / Players
// Seated / In Play) is published under its own activity-strip/ prefix by
// functions/broadcastActivityStrip (serverless-v2/services/lobby-broadcast).
//
// Derived from WEB_DOMAIN, like REFERRALS_URL/REFERRALS_API_URL and
// PUBLIC_API_URL/PLAYER_API_URL above. global-data-cdn deploys with
// `--stage live` in production, whose custom.cdnHost
// (services/global-data-cdn/serverless.yml) resolves
// ssm:/live/serverless/domain — set from the `production` GitHub
// Environment's DOMAIN variable, which is hijackpoker.com, not hijack.poker.
// So the CDN's real production host is cdn.hijackpoker.com, the same split
// that sends referrals to the web domain rather than the engine/auth one.
// Using DOMAIN here (as this constant originally did) built
// cdn.hijack.poker, a CloudFront distribution that doesn't exist.
export const GLOBAL_DATA_CDN_URL = `https://cdn.${WEB_DOMAIN}`;
export const DESCOPE_SESSION = process.env.REACT_APP_DESCOPE_SESSION;
export const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT;

export function getBackofficeUrl(): string {
  return (
    process.env.REACT_APP_BACKOFFICE_URL ||
    `https://backoffice.${process.env.REACT_APP_DOMAIN || 'hijack.poker'}`
  );
}

export const MARKETING_BASE_URL = 'https://www.hijackpoker.com';
export const HELP_CENTER_URL = 'https://help.hijackpoker.com';
export const PRICING_URL = `${MARKETING_BASE_URL}/pricing`;
export const TOURNAMENT_SCHEDULE_URL = `${MARKETING_BASE_URL}/tournament-schedule`;
export const PROMOS_URL = `${MARKETING_BASE_URL}/promos`;
export const TCH_REWARDS_URL = 'http://rewards.texascardhouse.com/';

/**
 * Build the login redirect URL. On localhost, uses REACT_APP_LOGIN_URL env var
 * to avoid the undefined.undefined hostname problem.
 */
export const getLoginRedirectUrl = (path = '/loginwebgl'): string => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === 'hijack.local') {
    return process.env.REACT_APP_LOGIN_URL || `${FRONT_OFFICE_LOCAL_URL}${path}`;
  }
  const segments = hostname.split('.');
  return `https://account.${segments[1]}.${segments[2]}${path}`;
};

/**
 * On localhost, suppress auto-redirects to the auth page.
 * Show a login button instead so devs can test locally.
 */
export const isAuthBypassed = (): boolean => {
  return window.location.hostname === 'localhost';
};
